import type { AgentTurnRow, AgentWidget } from 'aevocam-rest-api-spec'

export type ThinkingLevel = 'low' | 'high'

export const THINKING_MODELS: Record<ThinkingLevel, string> = {
  low: 'gpt-5-mini',
  high: 'gpt-5',
}

export type ChatMessage = {
  id: number
  role: 'user' | 'agent'
  content: string
  widgets?: AgentWidget[]
  /** Ephemeral-then-kept model reasoning for this reply */
  reasoning?: string
  /** True only for the first render after widgets arrive via the live stream */
  fromStream?: boolean
}

let nextMessageId = 1

export function allocMessageId() {
  return nextMessageId++
}

export function conversationStorageKey(persistenceKey: string) {
  return `agentConversation.${persistenceKey}`
}

export function formatConversationCostUsd(costUsd: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(costUsd)
}

function turnReasoningText(turn: AgentTurnRow): string | undefined {
  const parts: string[] = []
  for (const record of turn.reasoning ?? []) {
    if (record.summary?.length)
      parts.push(...record.summary)
    else if (record.content?.length)
      parts.push(...record.content)
  }
  const text = parts.join('\n').trim()
  return text || undefined
}

export function turnsToMessages(turns: AgentTurnRow[]): ChatMessage[] {
  const messages: ChatMessage[] = []
  for (const turn of turns) {
    if (turn.prompt)
      messages.push({ id: allocMessageId(), role: 'user', content: turn.prompt })

    const widgets: AgentWidget[] = []
    for (const tool of turn.tools ?? []) {
      if (tool.forUi)
        widgets.push(...tool.forUi)
    }

    const reasoning = turnReasoningText(turn)

    if (turn.reply)
      messages.push({ id: allocMessageId(), role: 'agent', content: turn.reply, widgets, reasoning })
    else if (turn.error)
      messages.push({ id: allocMessageId(), role: 'agent', content: `*Error:* ${turn.error}`, widgets, reasoning })
    else if (widgets.length > 0 || reasoning)
      messages.push({ id: allocMessageId(), role: 'agent', content: '', widgets, reasoning })
  }
  return messages
}
